/**
 * Order tracking hook using Firebase Realtime Database
 *
 * Listens to order status updates in real-time via Firebase RTDB.
 * Path: /restaurants/{restaurantId}/active_orders/{orderId}
 *
 * Falls back to polling the REST API if RTDB is not available or auth fails.
 *
 * Key design: waits for Firebase auth to be ready before subscribing to RTDB,
 * avoiding the race condition where onValue fires before auth is restored.
 *
 * Mobile resilience: RTDB null snapshots are debounced (3s) to handle
 * iOS/Android WebSocket reconnection where onValue briefly fires null
 * before re-syncing real data.
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { ref, onValue, off, type DatabaseReference } from 'firebase/database'
import { onAuthStateChanged } from 'firebase/auth'
import { database, auth, isFirebaseConfigured } from '@/config/firebase'
import { getEnvironmentConfig } from '@/config/environment'
import { useAppDispatch } from '@/store/hooks'
import {
  setOrderStatus,
  setEstimatedTime,
  setOrderNumber,
  setTrackingCode,
} from '@/store/orderSlice'
import type { OrderStatus } from '@/types/order'

interface OrderTrackingData {
  status: OrderStatus
  order_number: string
  tracking_code: string
  order_type: 'pickup' | 'delivery'
  customer_name: string
  items_count: number
  items_summary: string
  total: number
  currency: string
  payment_status: string
  estimated_ready_at: string | null
  placed_at: string
  updated_at: string
}

interface UseOrderTrackingOptions {
  /** Order ID to track */
  orderId: string | null
  /** Tracking code for public API fallback */
  trackingCode?: string | null
  /** Polling interval in ms (fallback when RTDB unavailable). Default: 10000 */
  pollingInterval?: number
  /** Whether tracking is enabled */
  enabled?: boolean
}

interface UseOrderTrackingReturn {
  /** Whether we're connected to real-time updates */
  isRealtime: boolean
  /** Whether we're using polling fallback */
  isPolling: boolean
  /** Last error if any */
  error: string | null
  /** Live tracking data from RTDB (null when using polling) */
  trackingData: OrderTrackingData | null
}

/**
 * Internal hook: resolves Firebase auth state before allowing RTDB subscriptions.
 * Returns isReady=true once onAuthStateChanged has fired at least once.
 */
function useFirebaseAuthReady(): { isAuthenticated: boolean; isReady: boolean } {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    if (!isFirebaseConfigured) {
      setIsReady(true)
      return
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user)
      setIsReady(true)
    })

    return () => unsubscribe()
  }, [])

  return { isAuthenticated, isReady }
}

/** Delay before treating an RTDB null snapshot as a real deletion (ms).
 *  Mobile browsers (iOS Safari, Android Chrome) aggressively kill WebSocket
 *  connections. On reconnect, Firebase RTDB may briefly fire onValue with null
 *  before re-syncing real data. This debounce prevents that from wiping state. */
const NULL_DEBOUNCE_MS = 3000

export function useOrderTracking({
  orderId,
  trackingCode,
  pollingInterval = 10000,
  enabled = true,
}: UseOrderTrackingOptions): UseOrderTrackingReturn {
  const dispatch = useAppDispatch()
  const { isAuthenticated, isReady: isAuthReady } = useFirebaseAuthReady()

  const dbRef = useRef<DatabaseReference | null>(null)
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const nullTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasReceivedData = useRef(false)
  const lastKnownStatus = useRef<OrderStatus | null>(null)

  const [isRealtime, setIsRealtime] = useState(false)
  const [isPolling, setIsPolling] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [trackingData, setTrackingData] = useState<OrderTrackingData | null>(null)

  const handleUpdate = useCallback((data: OrderTrackingData) => {
    setTrackingData(data)
    if (data.status) {
      lastKnownStatus.current = data.status
      dispatch(setOrderStatus(data.status))
    }
    if (data.estimated_ready_at) {
      dispatch(setEstimatedTime(data.estimated_ready_at))
    }
    if (data.order_number) {
      dispatch(setOrderNumber(data.order_number))
    }
    if (data.tracking_code) {
      dispatch(setTrackingCode(data.tracking_code))
    }
  }, [dispatch])

  // Clean up null debounce timer on unmount
  useEffect(() => {
    return () => {
      if (nullTimer.current) clearTimeout(nullTimer.current)
    }
  }, [])

  // Firebase RTDB subscription — waits for auth to resolve first
  useEffect(() => {
    if (!enabled || !orderId || !isAuthReady) return

    // RTDB rules require auth — skip if user isn't authenticated
    if (!isAuthenticated) return

    const isDbReady = database && typeof database.app !== 'undefined'
    if (!isDbReady) return

    const { restaurantId } = getEnvironmentConfig()
    if (!restaurantId) return

    try {
      const orderRef = ref(database, `restaurants/${restaurantId}/active_orders/${orderId}`)
      dbRef.current = orderRef

      const unsubscribe = onValue(orderRef, (snapshot) => {
        const data = snapshot.val()
        if (data) {
          // Real data arrived — cancel any pending null handler
          if (nullTimer.current) {
            clearTimeout(nullTimer.current)
            nullTimer.current = null
          }
          hasReceivedData.current = true
          setIsRealtime(true)
          handleUpdate(data as OrderTrackingData)
        } else if (hasReceivedData.current) {
          // Got null after having data. On mobile, this can be a transient
          // WebSocket reconnect artifact. Debounce before assuming deletion.
          if (!nullTimer.current) {
            nullTimer.current = setTimeout(() => {
              nullTimer.current = null
              hasReceivedData.current = false
              const terminalStatus = lastKnownStatus.current || 'completed'
              dispatch(setOrderStatus(terminalStatus as OrderStatus))
              setIsRealtime(false)
              setTrackingData(null)
            }, NULL_DEBOUNCE_MS)
          }
        }
      }, (err) => {
        console.warn('[OrderTracking] RTDB error, falling back to polling:', err.message)
        setIsRealtime(false)
        setError(err.message)
      })

      return () => {
        unsubscribe()
        if (nullTimer.current) {
          clearTimeout(nullTimer.current)
          nullTimer.current = null
        }
        if (dbRef.current) {
          off(dbRef.current)
          dbRef.current = null
        }
      }
    } catch {
      setIsRealtime(false)
    }
  }, [orderId, enabled, isAuthReady, isAuthenticated, handleUpdate, dispatch])

  // Polling fallback — runs when RTDB isn't providing real-time updates
  useEffect(() => {
    if (!enabled || isRealtime || !trackingCode) return
    // Wait for auth check to complete first — if auth succeeds, RTDB will handle it
    if (!isAuthReady) return

    setIsPolling(true)

    const poll = async () => {
      try {
        const { apiBaseUrl } = getEnvironmentConfig()
        const result = await fetch(
          `${apiBaseUrl}/restohub-api/orders/track/${trackingCode}/`
        ).then(r => r.json())

        if (result?.status) {
          handleUpdate({
            status: result.status as OrderStatus,
            order_number: result.order_number || '',
            tracking_code: result.tracking_code || '',
            estimated_ready_at: result.estimated_ready_at,
          } as OrderTrackingData)
        }
      } catch (err) {
        console.warn('[OrderTracking] Poll error:', err)
      }
    }

    // Poll immediately, then on interval
    poll()
    pollTimer.current = setInterval(poll, pollingInterval)

    return () => {
      if (pollTimer.current) {
        clearInterval(pollTimer.current)
        pollTimer.current = null
      }
      setIsPolling(false)
    }
  }, [enabled, isRealtime, trackingCode, pollingInterval, handleUpdate, isAuthReady])

  return { isRealtime, isPolling, error, trackingData }
}
